import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {AccountModel} from '../../../models/api/accountModel';
import {CardModel} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {ContactCardPanelId} from '../../../store/panels/panelStateBase';
import {ContactCardUnverifiedSenderBanner} from '../banner/contactCardUnverifiedSenderBanner';
import {ContactCardHeaderAccountContainer} from './contactCardHeaderAccountContainer';
import {ContactCardHeaderAvatar} from './contactCardHeaderAvatar';
import {ContactCardHeaderName} from './contactCardHeaderName';

/*
 * Props.
 */

interface ContactCardHeaderProps {
  panelId: ContactCardPanelId;
  visualContact: VisualContactModel;
  card?: CardModel;
  account?: AccountModel;
  isUnverified?: boolean;
  isLoading: boolean;
  isAccountLoading: boolean;
  isUserVisitor: boolean;
}

/*
 * Style.
 */

interface StyledHeaderWrapperDivProps {
  $shouldRenderBottomBorder: boolean;
}

const StyledHeaderWrapperDiv = styled.div<StyledHeaderWrapperDivProps>`
  padding: 16px 8px;
  display: flex;
  flex-flow: column;
  gap: 8px;
  overflow: hidden;
  min-height: 80px;

  ${(p) => maybeAddBottomStyles(p)};
`;

function maybeAddBottomStyles(p: StyledHeaderWrapperDivProps & ThemeContextType) {
  if (!p.$shouldRenderBottomBorder) {
    return css`
      padding-bottom: 8px;
    `;
  }
  return css`
    border-bottom: 1px solid ${p.theme.alphas.black30};
  `;
}

const StyledContactDetailsWrapperDiv = styled.div`
  flex: 0 0 48px;
  padding: 0 8px;
  display: flex;
  flex-flow: row;
  gap: 12px;
`;

interface StyledDetailsWrapperDivProps {
  $hasAccount?: boolean;
}

const StyledDetailsWrapperDiv = styled.div<StyledDetailsWrapperDivProps>`
  flex: 1;
  display: flex;
  flex-flow: column;
  justify-content: center;
  overflow: hidden;
  gap: 4px;
  ${(p) => maybeAddAccountCopyButtonSpacing(p)}
`;

function maybeAddAccountCopyButtonSpacing(p: StyledDetailsWrapperDivProps) {
  if (!p.$hasAccount) {
    return '';
  }
  return css`
    // Need to give some artificial space for the account copy button.
    padding-bottom: 4px;
    margin-bottom: -4px;
  `;
}

/*
 * Component.
 */

export const ContactCardHeader: FC<ContactCardHeaderProps> = (props) => {
  const {panelId, isLoading, isAccountLoading, visualContact, card, account, isUnverified, isUserVisitor} =
    props;

  return (
    <StyledHeaderWrapperDiv $shouldRenderBottomBorder={Boolean(card?.accountId && account) && !isUserVisitor}>
      <StyledContactDetailsWrapperDiv>
        <ContactCardHeaderAvatar
          visualContact={visualContact}
          account={account}
          card={card}
          isLoading={isLoading}
          isAccountLoading={isAccountLoading}
        />
        <StyledDetailsWrapperDiv $hasAccount={Boolean(account)}>
          <ContactCardHeaderName
            isLoading={isLoading}
            visualContact={visualContact}
            card={card}
            isUserVisitor={isUserVisitor}
          />
          <ContactCardHeaderAccountContainer
            panelId={panelId}
            account={account}
            card={card}
            isAccountLoading={isAccountLoading}
          />
        </StyledDetailsWrapperDiv>
      </StyledContactDetailsWrapperDiv>
      {maybeRenderUnverifiedBanner(isLoading, isUnverified)}
    </StyledHeaderWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderUnverifiedBanner(isLoading: boolean, isUnverified?: boolean) {
  if (!isUnverified || isLoading) {
    return null;
  }
  return <ContactCardUnverifiedSenderBanner />;
}
